import { useCallback, useEffect, useRef, useState } from 'react';
import { LIMITS, newDiagram, validateDiagram } from './model';
import { readWorkspace, saveWorkspace, saveDiagram } from './storage';
import { syncCanvasMermaid } from './mermaid';

export function useDiagramDocument() {
  const [document, setDocument] = useState(newDiagram);
  const current = useRef(document), history = useRef({ past: [], future: [] }), gesture = useRef(null);
  const [ready, setReady] = useState(false), [message, setMessage] = useState('Opening local workspace…');
  const queue = useRef(Promise.resolve()), generation = useRef(0);
  const dirty = useRef(false);
  const notice = useRef(false);
  const notify = useCallback(value => { notice.current = true; setMessage(value); }, []);
  const replace = useCallback(value => {
    const safe = validateDiagram(value);
    generation.current++; history.current = { past: [], future: [] }; gesture.current = null;
    current.current = safe; setDocument(safe);
    notice.current = false;
    dirty.current = true;
  }, []);
  useEffect(() => {
    let live = true;
    readWorkspace().then(value => { if (live && value) replace(value); if (live) setMessage('Local workspace ready'); })
      .catch(() => { if (live) setMessage('Local storage unavailable. Export JSON to keep your work.'); })
      .finally(() => { if (live) setReady(true); });
    return () => { live = false; };
  }, [replace]);
  useEffect(() => {
    const warn = event => { if (dirty.current) { event.preventDefault(); event.returnValue = ''; } };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, []);
  useEffect(() => {
    if (!ready) return undefined;
    const timer = setTimeout(() => {
      const value = current.current, epoch = generation.current;
      queue.current = queue.current.catch(() => {}).then(() => saveWorkspace(value)).then(() => { if (current.current === value) dirty.current = false; if (generation.current === epoch && !notice.current) setMessage('Draft saved locally'); }).catch(() => setMessage('Draft could not be saved. Export JSON to keep your work.'));
    }, 400);
    return () => clearTimeout(timer);
  }, [document, ready]);
  const change = useCallback((updater, record = true) => {
    const prev = current.current;
    const next = syncCanvasMermaid(prev, { ...prev, ...(typeof updater === 'function' ? updater(prev) : updater), updatedAt: Date.now() });
    if (next.elements.length > LIMITS.shapes || next.connectors.length > LIMITS.connectors) { setMessage('Diagram capacity reached (500 shapes / 1,000 connectors).'); return; }
    if (record && !gesture.current) { history.current.past = [...history.current.past.slice(-(LIMITS.history - 1)), prev]; history.current.future = []; }
    current.current = next; setDocument(next);
    notice.current = false;
    dirty.current = true;
  }, []);
  const begin = useCallback(() => { gesture.current = current.current; }, []);
  const end = useCallback(() => {
    if (gesture.current && gesture.current !== current.current) { history.current.past = [...history.current.past.slice(-(LIMITS.history - 1)), gesture.current]; history.current.future = []; setDocument({ ...current.current }); }
    gesture.current = null;
  }, []);
  const undo = useCallback(() => {
    const prev = history.current.past.pop(); if (!prev) return;
    history.current.future.push(current.current); current.current = prev; dirty.current = true; setDocument(prev);
  }, []);
  const redo = useCallback(() => {
    const next = history.current.future.pop(); if (!next) return;
    history.current.past.push(current.current); current.current = next; dirty.current = true; setDocument(next);
  }, []);
  const save = useCallback(async () => {
    const value = current.current;
    queue.current = queue.current.catch(() => {}).then(() => saveDiagram(value));
    await queue.current; if (current.current === value) dirty.current = false; setMessage('Diagram saved on this browser');
  }, []);
  return { document, current, change, replace, ready, message, setMessage: notify, begin, end, undo, redo, save, canUndo: !!history.current.past.length, canRedo: !!history.current.future.length };
}
